import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Star, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, REVIEW_SERVICES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { maybeRequestReviewAfterReading } from "../lib/playReview";

const PENDING_KEY = "arcana-pending-review";
const READING_PATHS = ["/lettura", "/piacentine", "/napoletane", "/rune", "/geomanzia", "/assistente"];

export function markReadingCompleted(service) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ service }));
  } catch {}
}

function consumePending() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const ReviewPrompt = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const prevPath = useRef(null);
  const [service, setService] = useState(null);
  const [visible, setVisible] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const serviceLabel = REVIEW_SERVICES.find((s) => s.v === service)?.label || service;

  useEffect(() => {
    const prev = prevPath.current;
    prevPath.current = pathname;
    if (!prev || prev === pathname) return;
    const leftReading = READING_PATHS.some((p) => prev.startsWith(p));
    const enteredReading = READING_PATHS.some((p) => pathname.startsWith(p));
    if (!leftReading || enteredReading) return;
    const pending = consumePending();
    if (!pending) return;
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
      maybeRequestReviewAfterReading();
      return;
    }
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/reviews/mine");
        if (data.reviewed) return;
        setRating(5);
        setText("");
        setSent(false);
        setService(pending.service);
        setVisible(true);
      } catch {}
    })();
  }, [pathname, user]);

  if (!visible) return null;

  const close = () => setVisible(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { service, text: text.trim(), rating });
      setSent(true);
      window.setTimeout(() => setVisible(false), 2600);
    } catch (e) {
      toast.error(fmtErr(e, "Recensione non inviata. Riprova."));
      if (e?.response?.status === 400) setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={close}
      data-testid="review-prompt-modal"
    >
      <div
        className="mystic-card max-w-sm w-full p-7 relative text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="review-prompt-close"
          className="absolute top-4 right-4 text-purple-300/60 hover:text-amber-300 transition-colors"
          aria-label="Chiudi"
        >
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="py-6" data-testid="review-prompt-sent">
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <p className="text-sm text-emerald-300 leading-relaxed" data-testid="review-thankyou-message">
              Grazie per la tua recensione!
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-2xl text-amber-100" data-testid="review-prompt-title">
              Com'è andata la tua esperienza?
            </h2>
            <div className="flex items-center justify-center gap-2 mt-5" data-testid="review-prompt-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  data-testid={`review-prompt-star-${n}`}
                  aria-label={`${n} stelle`}
                  className="transition-transform hover:scale-110"
                >
                  <Star className={`w-8 h-8 ${n <= rating ? "text-amber-400 fill-amber-400" : "text-purple-300/30"}`} />
                </button>
              ))}
            </div>
            <p className="text-xs text-purple-300/60 mt-4 leading-relaxed">
              Lascia una recensione sulla tua esperienza con Arcana.
            </p>
            <p className="text-[11px] text-amber-300/70 mt-2" data-testid="review-prompt-service">
              Servizio: {serviceLabel}
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="La tua esperienza, con parole tue…"
              className="input-dark mt-4 text-left"
              data-testid="review-prompt-text"
            />
            <div className="flex flex-col gap-3 mt-5">
              <button
                onClick={submit}
                disabled={busy || text.trim().length < 5}
                data-testid="review-prompt-submit"
                className="gold-btn py-3 text-sm tracking-wide disabled:opacity-50"
              >
                {busy ? "Invio…" : "LASCIA UNA RECENSIONE"}
              </button>
              <button
                onClick={close}
                data-testid="review-prompt-later"
                className="text-xs text-purple-300/60 hover:text-purple-200 transition-colors tracking-wide"
              >
                PIÙ TARDI
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
